import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/* ── Categorías ── */
const CAT_ORDER = [
  'cocktails','copas','refrescos','cervezas_botella','cervezas_grifo','vinos','zumos',
  'desayunos','aperitivos','raciones','hamburguesas','asados',
  'comida','postres','eventos',
  'cervezas','sin_alcohol',   // legacy — se muestran si hay productos
];

const CAT_LABELS = {
  cocktails:        'Cocktails',
  copas:            'Licores',
  refrescos:        'Refrescos',
  cervezas_botella: 'Cervezas',
  cervezas_grifo:   'De grifo',
  vinos:            'Vinos',
  zumos:            'Zumos',
  desayunos:        'Desayunos',
  aperitivos:       'Aperitivos',
  raciones:         'Raciones',
  hamburguesas:     'Hamburguesas',
  asados:           'Asados',
  comida:           'Comida',
  postres:          'Postres',
  eventos:          'Eventos',
  cervezas:         'Cervezas (legacy)',
  sin_alcohol:      'Sin alcohol (legacy)',
};

/* Fallback visual por categoría — gradiente + sigla */
const CAT_FALLBACK = {
  cocktails:        { bg: 'linear-gradient(135deg, oklch(35% 0.22 345), oklch(22% 0.14 340))', label: 'CKT', color: '#ff2d95' },
  copas:            { bg: 'linear-gradient(135deg, oklch(30% 0.18 295), oklch(20% 0.10 290))', label: 'LIC', color: '#c084fc' },
  refrescos:        { bg: 'linear-gradient(135deg, oklch(32% 0.18 195), oklch(22% 0.12 200))', label: 'REF', color: '#00f0ff' },
  cervezas_botella: { bg: 'linear-gradient(135deg, oklch(36% 0.18  75), oklch(24% 0.12  80))', label: 'CRV', color: '#ffd700' },
  cervezas_grifo:   { bg: 'linear-gradient(135deg, oklch(32% 0.15  75), oklch(22% 0.10  80))', label: 'GRF', color: '#d4a017' },
  vinos:            { bg: 'linear-gradient(135deg, oklch(30% 0.20  15), oklch(20% 0.14  20))', label: 'VNO', color: '#f87171' },
  zumos:            { bg: 'linear-gradient(135deg, oklch(36% 0.20  60), oklch(24% 0.16  65))', label: 'ZMO', color: '#fb923c' },
  desayunos:        { bg: 'linear-gradient(135deg, oklch(34% 0.14  65), oklch(24% 0.10  60))', label: 'DSY', color: '#fbbf24' },
  aperitivos:       { bg: 'linear-gradient(135deg, oklch(30% 0.12 130), oklch(22% 0.08 135))', label: 'APR', color: '#86efac' },
  raciones:         { bg: 'linear-gradient(135deg, oklch(30% 0.14  35), oklch(22% 0.10  30))', label: 'RAC', color: '#fb923c' },
  hamburguesas:     { bg: 'linear-gradient(135deg, oklch(28% 0.14  50), oklch(20% 0.10  45))', label: 'BRG', color: '#f59e0b' },
  asados:           { bg: 'linear-gradient(135deg, oklch(28% 0.16  25), oklch(20% 0.12  20))', label: 'ASA', color: '#fca5a5' },
  comida:           { bg: 'linear-gradient(135deg, oklch(28% 0.10  30), oklch(20% 0.08  25))', label: 'COM', color: '#d1d5db' },
  postres:          { bg: 'linear-gradient(135deg, oklch(35% 0.18 340), oklch(25% 0.12 335))', label: 'POS', color: '#f9a8d4' },
  eventos:          { bg: 'linear-gradient(135deg, oklch(30% 0.16 260), oklch(22% 0.10 265))', label: 'EVT', color: '#818cf8' },
  default:          { bg: 'linear-gradient(135deg, oklch(22% 0.01 258), oklch(17% 0.01 258))', label: '—',   color: '#6b7280' },
};

function catFallback(category) {
  return CAT_FALLBACK[category] || CAT_FALLBACK.default;
}

/* ── Helpers ── */
function priceLabel(product) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const prices = variants.map((v) => Number(v.price)).filter(Number.isFinite);
  if (prices.length) {
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `${min.toFixed(2)} €` : `${min.toFixed(2)} – ${max.toFixed(2)} €`;
  }
  return product.price > 0 ? `${product.price.toFixed(2)} €` : null;
}

function detailImage(product) {
  return product?.detailImageUrl || product?.imageUrl || '';
}

function thumbnailImage(product) {
  return product?.thumbnailImageUrl || product?.imageUrl || detailImage(product);
}

/* ── Iconos ── */
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconEye = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEyeOff = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const IconPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconImage = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

/* ── Modal de producto ── */
const EMPTY = {
  name: '', description: '', price: '', category: 'cocktails',
  imageUrl: '', detailImageUrl: '', thumbnailImageUrl: '',
  active: true, featured: false
};

function ProductModal({ product, onClose, onSaved }) {
  const [form,      setForm]      = useState(product ? {
    name: product.name, description: product.description || '',
    price: String(product.price), category: product.category,
    imageUrl: product.imageUrl || '',
    detailImageUrl: detailImage(product),
    thumbnailImageUrl: thumbnailImage(product),
    active: product.active, featured: product.featured,
    trackStock: Boolean(product.trackStock),
  } : { ...EMPTY, trackStock: false });
  const [ingredients, setIngredients] = useState([]);
  const [recipe, setRecipe] = useState(
    product && Array.isArray(product.recipe)
      ? product.recipe.map((r) => ({ ingredientId: String(r.ingredientId?._id || r.ingredientId || ''), quantity: String(r.quantity ?? '') }))
      : []
  );
  useEffect(() => { api.get('/admin/inventory/ingredients?active=1').then(setIngredients).catch(() => {}); }, []);
  const ingMap = Object.fromEntries(ingredients.map((i) => [String(i._id), i]));
  const addRecipeLine = () => setRecipe((r) => [...r, { ingredientId: ingredients[0]?._id ? String(ingredients[0]._id) : '', quantity: '' }]);
  const setRecipeLine = (idx, k, v) => setRecipe((r) => r.map((line, i) => i === idx ? { ...line, [k]: v } : line));
  const removeRecipeLine = (idx) => setRecipe((r) => r.filter((_, i) => i !== idx));
  // Guía de fabricación (solo cocktails de momento)
  const [prepGuide, setPrepGuide] = useState(
    product && Array.isArray(product.prepGuide) ? product.prepGuide.map(String) : []
  );
  const addGuideStep = () => setPrepGuide((g) => [...g, '']);
  const setGuideStep = (idx, v) => setPrepGuide((g) => g.map((s, i) => i === idx ? v : s));
  const removeGuideStep = (idx) => setPrepGuide((g) => g.filter((_, i) => i !== idx));
  const moveGuideStep = (idx, dir) => setPrepGuide((g) => {
    const to = idx + dir;
    if (to < 0 || to >= g.length) return g;
    const next = [...g];
    [next[idx], next[to]] = [next[to], next[idx]];
    return next;
  });
  const [detailPreview, setDetailPreview] = useState(product ? detailImage(product) : '');
  const [thumbPreview,  setThumbPreview]  = useState(product ? thumbnailImage(product) : '');
  const [uploading, setUploading] = useState(false);
  const [uploadingTarget, setUploadingTarget] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const detailFileRef = useRef();
  const thumbFileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    const localPreview = URL.createObjectURL(file);
    if (target === 'thumbnailImageUrl') setThumbPreview(localPreview);
    else setDetailPreview(localPreview);
    setUploading(true);
    setUploadingTarget(target);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const token = localStorage.getItem('neon_token');
      const res = await fetch('/api/products/upload', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al subir');
      set(target, data.url);
      if (target === 'thumbnailImageUrl') setThumbPreview(data.url);
      else setDetailPreview(data.url);
    } catch (e) { setError(e.message); }
    finally {
      setUploading(false);
      setUploadingTarget('');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const detail = String(form.detailImageUrl || '').trim();
      const thumb = String(form.thumbnailImageUrl || '').trim();
      const cleanRecipe = recipe
        .filter((r) => r.ingredientId && Number(r.quantity) > 0)
        .map((r) => ({ ingredientId: r.ingredientId, quantity: Number(r.quantity) }));
      const payload = {
        ...form,
        detailImageUrl: detail,
        thumbnailImageUrl: thumb,
        imageUrl: detail || thumb || form.imageUrl || '',
        price: parseFloat(form.price) || 0,
        trackStock: Boolean(form.trackStock),
        recipe: cleanRecipe,
        prepGuide: prepGuide.map((s) => s.trim()).filter(Boolean),
      };
      if (product) await api.patch(`/products/${product._id}`, payload);
      else         await api.post('/products', payload);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="np-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="np-modal">
        <div className="np-modal-head">
          <h3>{product ? 'Editar producto' : 'Nuevo producto'}</h3>
          <button type="button" className="np-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="np-form">
          <div className="np-image-area">
            <div className="np-image-panel">
              <label>Imagen de detalle</label>
              <button
                type="button"
                className="np-image-preview np-image-preview-detail"
                onClick={() => detailFileRef.current.click()}
                style={{ backgroundImage: detailPreview ? `url(${detailPreview})` : 'none' }}
              >
                {!detailPreview && <><IconImage /><span>Subir imagen grande</span></>}
                {uploading && uploadingTarget === 'detailImageUrl' && <div className="np-uploading">Subiendo…</div>}
              </button>
              <input ref={detailFileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={(e) => handleFile(e, 'detailImageUrl')} />
              <div className="np-image-url">
                <input
                  placeholder="URL para modal y vista ampliada"
                  value={form.detailImageUrl}
                  onChange={(e) => { set('detailImageUrl', e.target.value); setDetailPreview(e.target.value); }}
                />
              </div>
            </div>

            <div className="np-image-panel">
              <label>Miniatura de listado</label>
              <button
                type="button"
                className="np-image-preview np-image-preview-thumb"
                onClick={() => thumbFileRef.current.click()}
                style={{ backgroundImage: thumbPreview ? `url(${thumbPreview})` : 'none' }}
              >
                {!thumbPreview && <><IconImage /><span>Subir miniatura</span></>}
                {uploading && uploadingTarget === 'thumbnailImageUrl' && <div className="np-uploading">Subiendo…</div>}
              </button>
              <input ref={thumbFileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={(e) => handleFile(e, 'thumbnailImageUrl')} />
              <div className="np-image-url">
                <input
                  placeholder="URL para tarjetas y tablas"
                  value={form.thumbnailImageUrl}
                  onChange={(e) => { set('thumbnailImageUrl', e.target.value); setThumbPreview(e.target.value); }}
                />
              </div>
            </div>
          </div>

          <div className="np-fields">
            <div className="np-field-full">
              <label>Nombre *</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} required placeholder="Nombre del producto" />
            </div>
            <div className="np-field-full">
              <label>Descripción</label>
              <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} placeholder="Descripción breve" />
            </div>
            <div className="np-field-half">
              <label>Precio (€) *</label>
              <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => set('price', e.target.value)} required placeholder="0.00" />
            </div>
            <div className="np-field-half">
              <label>Categoría</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                {CAT_ORDER.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="np-checks">
              <label className="np-check">
                <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
                <span>Activo (visible en carta)</span>
              </label>
              <label className="np-check">
                <input type="checkbox" checked={form.featured} onChange={(e) => set('featured', e.target.checked)} />
                <span>Cocktail firma ⭐</span>
              </label>
              <label className="np-check">
                <input type="checkbox" checked={form.trackStock} onChange={(e) => set('trackStock', e.target.checked)} />
                <span>Controlar inventario por receta</span>
              </label>
            </div>
          </div>

          {/* ── Receta (ingredientes que descuenta cada venta) ── */}
          {form.trackStock && (
            <div className="np-recipe">
              <div className="np-recipe-head">
                <label>Receta · ingredientes a descontar por unidad vendida</label>
                <button type="button" className="neon-btn neon-btn-secondary np-recipe-add" onClick={addRecipeLine} disabled={!ingredients.length}>
                  + Ingrediente
                </button>
              </div>
              {!ingredients.length && <p className="np-recipe-empty">No hay ingredientes. Créalos primero en Inventario.</p>}
              {recipe.map((line, idx) => {
                const ing = ingMap[line.ingredientId];
                return (
                  <div className="np-recipe-row" key={idx}>
                    <select value={line.ingredientId} onChange={(e) => setRecipeLine(idx, 'ingredientId', e.target.value)}>
                      <option value="">— elegir —</option>
                      {ingredients.map((i) => <option key={i._id} value={String(i._id)}>{i.name}</option>)}
                    </select>
                    <input type="number" step="0.01" min="0" placeholder="Cantidad" value={line.quantity} onChange={(e) => setRecipeLine(idx, 'quantity', e.target.value)} />
                    <span className="np-recipe-unit">{ing?.unit || ''}</span>
                    <button type="button" className="np-recipe-del" onClick={() => removeRecipeLine(idx)} title="Quitar">✕</button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Guía de fabricación (solo cocktails de momento) ── */}
          {form.category === 'cocktails' && (
            <div className="np-recipe np-prepguide">
              <div className="np-recipe-head">
                <label>Guía de fabricación · pasos que verá el personal en Pedidos</label>
                <button type="button" className="neon-btn neon-btn-secondary np-recipe-add" onClick={addGuideStep}>
                  + Paso
                </button>
              </div>
              {!prepGuide.length && (
                <p className="np-recipe-empty">Sin pasos. Añade el primero con "+ Paso".</p>
              )}
              {prepGuide.map((step, idx) => (
                <div className="np-guide-row" key={idx}>
                  <span className="np-guide-num">{idx + 1}</span>
                  <input
                    placeholder={`Paso ${idx + 1} — ej. "Enfriar la copa con hielo"`}
                    value={step}
                    onChange={(e) => setGuideStep(idx, e.target.value)}
                  />
                  <button type="button" className="np-guide-move" onClick={() => moveGuideStep(idx, -1)} disabled={idx === 0} title="Subir">↑</button>
                  <button type="button" className="np-guide-move" onClick={() => moveGuideStep(idx, 1)} disabled={idx === prepGuide.length - 1} title="Bajar">↓</button>
                  <button type="button" className="np-recipe-del" onClick={() => removeGuideStep(idx)} title="Quitar">✕</button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="np-error">{error}</p>}

          <div className="np-actions">
            <button type="button" className="neon-btn neon-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="neon-btn neon-btn-primary" disabled={saving || uploading}>
              {saving ? 'Guardando…' : product ? 'Guardar cambios' : 'Crear producto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Tarjeta de producto ── */
function ProductCard({ product, onEdit, onToggle, onDelete }) {
  const fb      = catFallback(product.category);
  const img     = thumbnailImage(product);
  const hasImg  = !!img;
  const price   = priceLabel(product);

  return (
    <div className={`np-grid-card${product.active ? '' : ' np-grid-card--inactive'}`}>
      {/* Imagen / fallback */}
      <div
        className="np-grid-img"
        style={hasImg
          ? { backgroundImage: `url(${img})` }
          : { background: fb.bg }
        }
      >
        {!hasImg && (
          <span className="np-grid-fallback-label" style={{ color: fb.color }}>
            {fb.label}
          </span>
        )}
        {product.featured && <span className="np-grid-firma">⭐</span>}
        {!product.active && <span className="np-grid-hidden-badge">Oculto</span>}
      </div>

      {/* Info */}
      <div className="np-grid-body">
        <p className="np-grid-name">{product.name}</p>
        <span className="np-cat-chip">{CAT_LABELS[product.category] || product.category}</span>
        {price
          ? <span className="np-grid-price">{price}</span>
          : <span className="np-consultar">Consultar</span>
        }
      </div>

      {/* Acciones */}
      <div className="np-grid-actions">
        <button
          type="button"
          className={`np-toggle ${product.active ? 'np-toggle-on' : 'np-toggle-off'}`}
          onClick={() => onToggle(product)}
          title={product.active ? 'Desactivar' : 'Activar'}
        >
          {product.active ? <><IconEye /> Activo</> : <><IconEyeOff /> Inactivo</>}
        </button>
        <div className="np-row-actions">
          <button type="button" className="np-action-btn np-edit"   onClick={() => onEdit(product)}   title="Editar"><IconEdit /></button>
          <button type="button" className="np-action-btn np-delete" onClick={() => onDelete(product)} title="Eliminar"><IconTrash /></button>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ── */
export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [modal,    setModal]    = useState(null);   // null | 'new' | product
  const [search,   setSearch]   = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [showInactive, setShowInactive] = useState(true);

  const load = () => api.get('/products').then(setProducts).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!confirm(`¿Eliminar "${p.name}"?`)) return;
    await api.del(`/products/${p._id}`);
    load();
  };
  const toggleActive = async (p) => {
    await api.patch(`/products/${p._id}`, { active: !p.active });
    load();
  };
  const onSaved = () => { setModal(null); load(); };

  /* Categorías presentes (sin duplicados, en orden) */
  const presentCats = CAT_ORDER.filter((c) => products.some((p) => p.category === c));

  /* Filtrado */
  const filtered = products.filter((p) => {
    if (!showInactive && !p.active) return false;
    if (activeCat !== 'all' && p.category !== activeCat) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) ||
             (CAT_LABELS[p.category] || '').toLowerCase().includes(q);
    }
    return true;
  });

  const activeCount   = products.filter((p) => p.active).length;
  const inactiveCount = products.filter((p) => !p.active).length;

  return (
    <div className="neon-admin-page">
      {/* Cabecera */}
      <div className="neon-admin-page-head">
        <div>
          <h2>Productos</h2>
          <p className="neon-admin-subtitle">
            {activeCount} activos · {inactiveCount} ocultos · {products.length} total
          </p>
        </div>
        <div className="neon-admin-head-right">
          <input
            className="np-search"
            placeholder="Buscar…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setActiveCat('all'); }}
          />
          <label className="np-inactive-toggle">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>Ver ocultos</span>
          </label>
          <button type="button" className="neon-btn neon-btn-primary np-btn-new" onClick={() => setModal('new')}>
            <IconPlus /> Nuevo
          </button>
        </div>
      </div>

      {/* Filtro por categoría */}
      {!search && (
        <div className="np-cat-rail" role="tablist" aria-label="Filtrar por categoría">
          <button
            type="button"
            role="tab"
            aria-selected={activeCat === 'all'}
            className={`np-cat-btn${activeCat === 'all' ? ' active' : ''}`}
            onClick={() => setActiveCat('all')}
          >
            Todos <span className="np-cat-count">{products.length}</span>
          </button>
          {presentCats.map((c) => {
            const count = products.filter((p) => p.category === c).length;
            return (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={activeCat === c}
                className={`np-cat-btn${activeCat === c ? ' active' : ''}`}
                onClick={() => setActiveCat(c)}
              >
                {CAT_LABELS[c]} <span className="np-cat-count">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="neon-orders-empty">No hay productos con los filtros seleccionados.</div>
      ) : (
        <div className="np-grid">
          {filtered.map((p) => (
            <ProductCard
              key={p._id}
              product={p}
              onEdit={setModal}
              onToggle={toggleActive}
              onDelete={del}
            />
          ))}
        </div>
      )}

      {modal && (
        <ProductModal
          product={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
